import type { SourceImage } from "./imageTypes";

export interface PreparedInputImage {
  blob: Blob;
  fileName: string;
  baseFile: File;
  isSvg: boolean;
}

let heicModule: Promise<typeof import("heic2any")> | null = null;

const loadHeic2Any = async () => {
  if (!heicModule) {
    heicModule = import("heic2any");
  }
  const mod = await heicModule;
  return mod.default;
};

export const isHeicFile = (file: File) =>
  file.type === "image/heic" ||
  file.type === "image/heif" ||
  file.name.toLowerCase().endsWith(".heic") ||
  file.name.toLowerCase().endsWith(".heif");

export const isSvgFile = (file: File) =>
  file.type === "image/svg+xml" || file.name.toLowerCase().endsWith(".svg");

export const isSupportedInputFile = (file: File) =>
  file.type.startsWith("image/") || isHeicFile(file) || isSvgFile(file);

export const getSupportedImageFiles = (files: FileList | File[]) =>
  Array.from(files).filter(isSupportedInputFile);

export const createFileList = (files: File[]) => {
  const dataTransfer = new DataTransfer();
  files.forEach((file) => dataTransfer.items.add(file));
  return dataTransfer.files;
};

export const createSourceFromBlob = (
  blob: Blob,
  fileName: string,
  fillWhite: boolean
): Promise<SourceImage> =>
  new Promise((resolve, reject) => {
    const img = new Image();
    const srcUrl = URL.createObjectURL(blob);
    img.crossOrigin = "anonymous";
    img.onload = () => {
      if (!fillWhite) {
        resolve({ blob, url: srcUrl, name: fileName, width: img.width, height: img.height });
        return;
      }

      URL.revokeObjectURL(srcUrl);
      const canvas = document.createElement("canvas");
      canvas.width = img.width;
      canvas.height = img.height;
      const ctx = canvas.getContext("2d")!;
      if (fillWhite) {
        ctx.fillStyle = "#ffffff";
        ctx.fillRect(0, 0, canvas.width, canvas.height);
      }
      ctx.drawImage(img, 0, 0);
      canvas.toBlob((pngBlob) => {
        if (!pngBlob) {
          reject(new Error("Failed to encode image"));
          return;
        }
        const url = URL.createObjectURL(pngBlob);
        resolve({ blob: pngBlob, url, name: fileName, width: img.width, height: img.height });
      }, "image/png");
    };
    img.onerror = () => {
      URL.revokeObjectURL(srcUrl);
      reject(new Error("Image load failed"));
    };
    img.src = srcUrl;
  });

export const prepareInputImage = async (file: File): Promise<PreparedInputImage> => {
  let blob: Blob = file;
  let fileName = file.name;

  if (isHeicFile(file)) {
    const heic2any = await loadHeic2Any();
    const converted = await heic2any({
      blob: file,
      toType: "image/jpeg",
      quality: 1,
    });
    blob = Array.isArray(converted) ? converted[0] : converted;
    fileName = fileName.replace(/\.(heic|heif)$/i, ".jpg");
  }

  const baseType = (blob as File).type || blob.type || file.type || "image/png";
  return {
    blob,
    fileName,
    baseFile: new File([blob], fileName, { type: baseType }),
    isSvg: isSvgFile(file),
  };
};

export const createBaseSource = (prepared: PreparedInputImage) =>
  createSourceFromBlob(prepared.blob, prepared.fileName, prepared.isSvg);

export const createTransparentSource = async (
  prepared: PreparedInputImage,
  removeBackground: (blob: Blob) => Promise<Blob>
) => {
  const bgRemovedBlob = await removeBackground(prepared.blob);
  return createSourceFromBlob(bgRemovedBlob, prepared.fileName, false);
};
